/**
 * PATH: apps/quote/src/components/builder/panels/SnapshotsPanel.tsx
 * FILE_ID: QB-PANEL-SNAPSHOTS-001
 * PURPOSE: Right-drawer panel — capture/list/delete point-in-time
 *          snapshots of the current quote.
 * WHY: Estimators want a one-click "Save snapshot" before destructive
 *      bulk edits, plus a timeline view showing label, totals, and
 *      author of every prior capture.
 * FLOW:
 *   ┌──────────────────┐    ┌────────────────────┐    ┌──────────────┐
 *   │ Save snapshot    │ →  │ POST /snapshots/   │ →  │ Refetch list │
 *   │ (label optional) │    │ server captures    │    │ + toast      │
 *   └──────────────────┘    │ QuoteDetail blob   │    └──────────────┘
 *                           └────────────────────┘
 * DEPENDENCIES:
 *   - QB-API-SNAPSHOTS-001 (snapshotsApi)
 * RELATED:
 *   - panels/RowInspectorPanel.tsx — separate concern (row-level state)
 *   - panels/HistoryPanel.tsx — granular row-level diffs (logger.ChangeLog)
 *
 * NOT IN SCOPE: restore-from-snapshot, diff-vs-snapshot. Both deferred
 * because they are destructive and need explicit UX, not a one-line
 * button. They live as a follow-up MR.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Camera, Trash2, Loader2, Clock, User as UserIcon, AlertCircle,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { quoteApi } from '@/lib/quoteApi'
import type { QuoteSnapshotListItem } from '@/lib/quoteApi'

interface SnapshotsPanelProps {
  quoteId: string
}

function timeAgo(iso: string): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const sec = Math.max(1, Math.floor((Date.now() - t) / 1000))
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 48) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}d ago`
  return new Date(t).toLocaleDateString()
}

const SOURCE_LABELS: Record<string, { label: string; tone: string }> = {
  manual: { label: 'Manual', tone: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
  auto_save: { label: 'Auto', tone: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
  pre_bulk: { label: 'Pre-bulk', tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  pre_send: { label: 'Pre-send', tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
}

export default function SnapshotsPanel({ quoteId }: SnapshotsPanelProps) {
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const queryKey = ['quote-snapshots', quoteId]

  const { data: snapshots, isLoading } = useQuery({
    queryKey,
    queryFn: () => quoteApi.snapshots.list(quoteId),
    enabled: Boolean(quoteId),
  })

  const createMutation = useMutation({
    mutationFn: () =>
      quoteApi.snapshots.create(quoteId, {
        label: label.trim(),
        note: note.trim(),
        source: 'manual',
      }),
    onSuccess: () => {
      setLabel('')
      setNote('')
      setError(null)
      queryClient.invalidateQueries({ queryKey })
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to save snapshot'
      setError(msg)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (snapId: string) => quoteApi.snapshots.remove(quoteId, snapId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  return (
    <div className="h-full flex flex-col">
      {/* Header — capture form */}
      <div className="flex-shrink-0 p-3 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/40 dark:to-purple-950/40">
        <div className="flex items-center gap-2 mb-2">
          <Camera className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
          <span className="text-sm font-semibold text-slate-900 dark:text-white">
            Save snapshot
          </span>
        </div>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (optional) — e.g. 'Before margin push'"
          className="w-full px-2 py-1.5 text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white mb-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          maxLength={120}
        />
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional)"
          rows={2}
          className="w-full px-2 py-1.5 text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
        <button
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending}
          className={cn(
            'w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
            createMutation.isPending
              ? 'bg-indigo-300 text-white cursor-wait'
              : 'bg-indigo-600 hover:bg-indigo-700 text-white',
          )}
        >
          {createMutation.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Camera className="w-3.5 h-3.5" />
          )}
          {createMutation.isPending ? 'Capturing…' : 'Capture snapshot'}
        </button>
        {error && (
          <div className="mt-2 flex items-start gap-1 text-[11px] text-red-600 dark:text-red-300">
            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* List — timeline of snapshots */}
      <div className="flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : !snapshots || snapshots.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Clock className="w-6 h-6 text-slate-300 mb-2" />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              No snapshots yet
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xs">
              Capture a snapshot before destructive edits so you can compare
              numbers later or roll back if needed.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {snapshots.map((snap) => (
              <SnapshotCard
                key={snap.id}
                snap={snap}
                onDelete={() => deleteMutation.mutate(snap.id)}
                isDeleting={deleteMutation.isPending && deleteMutation.variables === snap.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function SnapshotCard({
  snap,
  onDelete,
  isDeleting,
}: {
  snap: QuoteSnapshotListItem
  onDelete: () => void
  isDeleting: boolean
}) {
  const totals = snap.totals ?? {}
  const charge = Number(totals.chargeout_total ?? 0)
  const margin = Number(totals.margin_percent ?? 0)
  const lineCount = Number(totals.line_count ?? 0)
  const sourceMeta = SOURCE_LABELS[snap.source] ?? SOURCE_LABELS.manual

  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2.5 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span
              className={cn(
                'px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider',
                sourceMeta.tone,
              )}
            >
              {sourceMeta.label}
            </span>
            {snap.label && (
              <span className="text-xs font-medium text-slate-900 dark:text-white truncate">
                {snap.label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400">
            <Clock className="w-2.5 h-2.5" />
            <span>{timeAgo(snap.created_at)}</span>
            {snap.created_by_name && (
              <>
                <span className="opacity-30">•</span>
                <UserIcon className="w-2.5 h-2.5" />
                <span className="truncate">{snap.created_by_name}</span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-1 rounded transition-colors"
          title="Delete snapshot"
        >
          {isDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
        </button>
      </div>

      {snap.note && (
        <p className="text-[11px] text-slate-600 dark:text-slate-300 italic mb-2 line-clamp-2">
          {snap.note}
        </p>
      )}

      {/* Totals strip */}
      <div className="grid grid-cols-3 gap-1.5 text-center">
        <div className="rounded bg-slate-50 dark:bg-slate-800/50 px-1.5 py-1">
          <div className="text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-semibold">
            Total
          </div>
          <div className="text-[11px] font-semibold text-slate-900 dark:text-white tabular-nums">
            {formatCurrency(charge)}
          </div>
        </div>
        <div className="rounded bg-slate-50 dark:bg-slate-800/50 px-1.5 py-1">
          <div className="text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-semibold">
            Margin
          </div>
          <div
            className={cn(
              'text-[11px] font-semibold tabular-nums',
              margin >= 20
                ? 'text-emerald-600 dark:text-emerald-300'
                : margin >= 10
                ? 'text-amber-600 dark:text-amber-300'
                : 'text-red-600 dark:text-red-300',
            )}
          >
            {margin.toFixed(1)}%
          </div>
        </div>
        <div className="rounded bg-slate-50 dark:bg-slate-800/50 px-1.5 py-1">
          <div className="text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-semibold">
            Lines
          </div>
          <div className="text-[11px] font-semibold text-slate-900 dark:text-white tabular-nums">
            {lineCount}
          </div>
        </div>
      </div>
    </div>
  )
}
